import React, { Suspense, lazy, useRef } from 'react';
import { autobind } from 'core-decorators';
import { Col, Row, UncontrolledAlert } from 'reactstrap';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';

import ContentManager from 'src/componentes/content-manager';
import errorHandler from 'src/utils/error-handler';
import { capitalizeFirstLetter } from 'src/utils/string';
import StatusRelatorioLookup from 'src/lookups/status-relatorio-acompanhamento';
import http from 'src/services/httpService';
import httpSelector from 'src/services/httpSelector';
import GraficoValores from 'src/paginas/item/valor/componentes/grafico';
import LoadingContainer from 'src/componentes/loading-container';
import Tabs from 'src/componentes/tabs/tabs';
import Tab from 'src/componentes/tabs/tab';
import AcaoEdit from 'src/paginas/acao/edit';
import Confirm from 'src/componentes/message-box/confirm';
import {
  allColumnsCard,
  fetchStatusDisponiveis,
  fetchCard,
  fetchStatus,
} from 'src/paginas/acao/kanban/components/functions';
import InfoPanel from 'src/componentes/info-panel';
import ReactTooltip from 'react-tooltip';
import ModalFormCategoryLayout from 'src/componentes/modal-form-layout/ModalFormCategoryLayout';
import useCollapsesScrollHeight from 'src/componentes/modal-form-layout/useCollapsesScrollHeight';
import ArquivoUploadAnexos from 'src/componentes/arquivo-upload/anexos';
import SvgIcon from 'src/componentes/svg-icon';
import css from 'src/utils/css';
import LoadingScreen from 'src/componentes/LoadingScreen';
import DatePicker from 'src/componentes/date-picker';

import View from './view-list';
import AcaoList from '../acao/select-list';
import EstratificacaoPareto from 'src/paginas/EstratificacaoPareto';
import DesenharCampoCustomizado from '../item/campo-customizado/componentes/desenhar';
import { orientacaoTextoForEdit } from 'src/utils/html';

const PontosProblematicosList = lazy(() => import('./ponto-problematico'));
const PontosProblematicosListOption = lazy(() =>
  import('./ponto-problematico/listOptions')
);
const ValidacaoRestricao = lazy(() => import('../item/validacao-restricao'));
const Historico = lazy(() => import('../versao-alteracao'));
const ItemLink = lazy(() => import('../item/link'));
const ProjetosList = lazy(() => import('../projeto/select-list'));
const ReactQuill = lazy(() => import('src/componentes/quill'));

const baseClasses = css`
  .colunaValor {
    text-align: center;

    & > span {
      display: block;
    }
  }

  .campoResumoInline {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin-right: 1rem;
    margin-bottom: 0.5rem;
    white-space: nowrap;
  }

  .campoResumoLabel {
    color: #71717a;
    font-size: 16px;
    font-weight: 600;
  }

  .campoResumoValor {
    color: #09090b;
    font-size: 16px;
    font-weight: 700;
  }

  .campoResumoCopiar {
    font-size: 16px !important;
    font-weight: 700 !important;
    cursor: pointer;
  }

  .infoPanelOrientacao {
    margin-left: 8px !important;
    margin-right: 8px !important;
    margin-top: 16px !important;
    margin-bottom: 16px !important;
  }

  .resumoLinhaItem,
  .resumoLinhaValores {
    padding-left: 8px;
    padding-right: 8px;
  }

  .campoResumoInlineControle {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin-right: 1rem;
    margin-bottom: 0.5rem;

    .form-group {
      margin-bottom: 0;
    }

    .select .Select-value-label,
    .Select__single-value {
      color: #09090b !important;
      font-size: 16px !important;
      font-weight: 700 !important;
    }
  }

`;

const RA_CAT_ANALISE = 'relatorio-cat-analise';
const RA_CAT_CAUSA = 'relatorio-cat-causa';
const RA_CAT_ACAO = 'relatorio-cat-acao';

const RA_SEC_GRAFICO = 'relatorio-sec-grafico';
const RA_SEC_PLANEJADO = 'relatorio-sec-planejado';
const RA_SEC_EXECUTADO = 'relatorio-sec-executado';
const RA_SEC_FATOS = 'relatorio-sec-fatos';
const RA_SEC_INFO_COMPLEMENTARES = 'relatorio-sec-info-complementares';
const RA_SEC_ESTRATIFICACAO = 'relatorio-sec-estratificacao-pareto';
const RA_SEC_CAUSA = 'relatorio-sec-causa';
const RA_SEC_PLANO_ACAO = 'relatorio-sec-plano-acao';
const RA_SEC_PROJETOS = 'relatorio-sec-projetos';

@autobind
class FormEdit extends React.Component {
  static defaultProps = {
    refreshModel: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      saved: false,
      planoCompleto: false,
      labelPlanoCompleto: (
        <FormattedMessage
          id="label.exibirPlanoCompleto"
          values={{ planodeacao: this.props.resources.planodeacao }}
        />
      ),
      allColumnsCard: [],
      allColumnsKanban: [],
      columnsCard: null,
      columnsKanban: null,
      historicoRefreshToken: 0,
      layoutSyncKey: 0,
      paretoChartKey: 0,
    };
  }

  handleParetoSectionCollapse(isOpen) {
    if (isOpen) {
      this.setState((prev) => ({
        paretoChartKey: prev.paretoChartKey + 1,
      }));
    }
  }

  handleInfoPanelDismiss() {
    this.setState((prev) => ({
      layoutSyncKey: prev.layoutSyncKey + 1,
    }));
  }

  colors = [
    '#000000',
    '#e60000',
    '#ff9900',
    '#ffff00',
    '#008a00',
    '#0066cc',
    '#9933ff',
    '#ffffff',
    '#facccc',
    '#ffebcc',
    '#ffffcc',
    '#cce8cc',
    '#cce0f5',
    '#ebd6ff',
    '#bbbbbb',
    '#f06666',
    '#ffc266',
    '#ffff66',
    '#66b966',
    '#66a3e0',
    '#c285ff',
    '#888888',
    '#a10000',
    '#b26b00',
    '#b2b200',
    '#006100',
    '#0047b2',
    '#6b24b2',
    '#444444',
    '#5c0000',
    '#663d00',
    '#666600',
    '#003700',
    '#002966',
    '#3d1466',
  ];

  formats = [
    'font',
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'color',
    'background',
    'align',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
    'mb',
  ];

  modules = {
    toolbar: [
      [{ font: [] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ color: this.colors }, { background: [] }],
      [{ align: [] }],
      [
        { list: 'ordered' },
        { list: 'bullet' },
        { indent: '-1' },
        { indent: '+1' },
      ],
      ['link', 'image'],
      ['clean'],
      [{ mb: [0, 1, 2, 3, 4, 5] }],
    ],
  };

  componentDidMount() {
    this.didMountCalls();
  }

  refreshModel() {
    this.setState({
      saved: !this.state.saved,
      historicoRefreshToken: this.state.historicoRefreshToken + 1,
    });
  }

  handleTabSelect = (tabIndex) => {
    if (tabIndex === 1) {
      this.setState({
        historicoRefreshToken: this.state.historicoRefreshToken + 1,
      });
    }
  };

  async didMountCalls() {
    httpSelector
      .getHttp(this.props.mode)
      .post(`/AcaoConfiguracao/ObterConfiguracoes`, {})
      .then(async (response) => {
        let allColumnsCardLocal = await allColumnsCard(
          this.props.intl,
          this.props.resources,
          response.data.model
        );
        let columnsCard = await fetchCard(
          allColumnsCardLocal,
          this.props.viewType
        );
        let allColumnsKanban = await fetchStatusDisponiveis(
          this.props.viewType
        );
        let columnsKanban = await fetchStatus(
          allColumnsKanban,
          this.props.viewType
        );

        this.setState({
          allColumnsCard: allColumnsCardLocal,
          columnsCard: columnsCard,
          allColumnsKanban: allColumnsKanban,
          columnsKanban: columnsKanban,
        });
      })
      .catch((error) => {
        errorHandler(error);
      });
  }

  handlePlanoCompletoClick() {
    let { resources, intl } = this.props;
    this.setState({
      saved: !this.state.saved,
      planoCompleto: !this.state.planoCompleto,
      labelPlanoCompleto: !this.state.planoCompleto
        ? intl.formatMessage(
            { id: 'label.ocultarPlanoCompleto' },
            { planodeacao: resources.planodeacao }
          )
        : intl.formatMessage(
            { id: 'label.exibirPlanoCompleto' },
            { planodeacao: resources.planodeacao }
          ),
    });
  }

  handleAssociarClick() {
    ContentManager.addContent(
      <AcaoList
        showSearch={true}
        params={{ relatorioIdIgnorar: this.props.model.getModel('id').value }}
        multiple
        showModal
        disableGantt={true}
        disableDelete={true}
        select={true}
        selectedCallback={this.handleCustomSelectSearch}
      />
    );
  }

  handleCustomSelectSearch(itens) {
    http
      .post(`/Acao/AssociarAcoes`, {
        relatorioId: this.props.model.getModel('id').value,
        acoes: itens,
      })
      .then(() => {
        this.setState({
          saved: !this.state.saved,
        });
      })
      .catch((error) => {
        errorHandler(error);
      });
  }

  handleChangeStatus(value) {
    this.props.model.getModel('statusDefasadoAlterado').requestChange(false);

    //se for trocar o status de Defasado para pendente
    if (value != null && this.props.model.getModel('status').value != null) {
      if (value.id == 0 && this.props.model.getModel('status').value.id == 2) {
        this.props.model.getModel('statusDefasadoAlterado').requestChange(true);
      }
    }

    this.props.model.getModel('status').requestChange(value);
  }

  handleChangeText(field, text) {
    this.props.model.getModel(field).requestChange(text);
  }

  handleCopiarRelatorio(relatorioId, relatorioOrigemId) {
    let model = this.props.model;

    //guarda o gráfico para não precisar gerar novamente e melhorar o tempo de processamento da cópia
    const grafico = model.getModel('grafico').value;

    let allowEdit = model.getModel('permissions').getModel('allowEdit').value;

    this.setState({
      isLoading: true,
    });

    http
      .post(`/RelatorioAcompanhamento/CopiarRelatorio`, {
        relatorioId: relatorioId,
        relatorioOrigemId: relatorioOrigemId,
      })
      .then((response) => {
        model.requestChange({
          ...response.data,
          permissions: {
            ...(response.data?.permissions || {}),
            allowEdit,
          },
          //atualiza o gráfico no novo model
          grafico,
        });

        this.setState({
          isLoading: false,
        });
      })
      .catch((error) => {
        this.setState({
          isLoading: false,
        });
        errorHandler(error);
      });
  }

  handleSelect(lista) {
    let { intl, resources } = this.props;
    let relatorioOrigem = lista && lista[0];

    let relatorioId = this.props.model.getModel('id').value;
    let relatorioOrigemId = relatorioOrigem && relatorioOrigem.id;

    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage(
          { id: 'desejaCopiarConteudoRelatorioAcompanhamento' },
          {
            relatoriodeacompanhamento: resources.relatoriodeacompanhamento,
            acoes: resources.acoes,
          }
        )}
        handleConfirm={this.handleCopiarRelatorio.bind(
          this,
          relatorioId,
          relatorioOrigemId
        )}
      />
    );
  }

  handleDeleteClick(acao) {
    if (
      !acao ||
      !acao.item ||
      (this.props.model.getModel('item').value &&
        this.props.model.getModel('item').value.id != acao.item.id) ||
      (this.props.model.getModel('id') &&
        this.props.model.getModel('id').value !=
          (acao.relatorioAcompanhamento && acao.relatorioAcompanhamento.id))
    ) {
      ContentManager.addContent(
        <Confirm
          message={
            <FormattedMessage
              id="aAcaoSeraSomenteDesvinculadaENaoApagada"
              values={{
                planodeacao: this.props.resources.planodeacao,
                relatoriodeacompanhamento:
                  this.props.resources.relatoriodeacompanhamento,
              }}
            />
          }
          handleConfirm={this.handleConfirmdesvincularAcao.bind(this, acao)}
        />
      );
    } else {
      ContentManager.addContent(
        <ValidacaoRestricao
          url="/Acao/ValidarRestricoes"
          urlExclusao="/Acao/AtualizarStatusExcluido"
          item={acao}
          onClose={this.refreshModel}
        />
      );
    }
  }

  handleConfirmdesvincularAcao(acao) {
    let model = this.props.model;
    let pontosModel = model.getModel('pontosProblematicos').value;

    http
      .post(`/Acao/DesvincularRelatorioAcompanhamento`, {
        acaoId: acao.id,
        relatorioId: this.props.model.getModel('id').value,
      })
      .then(() => {
        pontosModel &&
          pontosModel.map((ponto) => {
            const acoes = ponto.acoes;
            const index =
              acoes && acoes.findIndex((prop) => prop.id === acao.id);

            if (index != -1) ponto.acoes.splice(index, 1);
          });

        model.getModel('pontosProblematicos').requestChange(pontosModel);
        this.refreshModel();
      })
      .catch((error) => {
        errorHandler(error);
      });
  }

  handleOpenRelatorios() {
    ContentManager.addContent(
      <View modelValue={null} selectedCallback={this.handleSelect} select />
    );
  }

  handleDescricaoClick(item, tab = null) {
    ContentManager.addContent(
      <AcaoEdit
        defaultActiveKey={tab}
        handleClose={this.handleCloseEdit}
        item={item}
        parameters={{ ...this.props.parameters }}
        onDeleting={this.handleDeleteClick}
      ></AcaoEdit>
    );
  }

  handleDesvincularPontoProblematicoClick(item) {
    this.props.model.getModel('acaoSelecionadaAtual').requestChange(item);
    ContentManager.addContent(
      <PontosProblematicosListOption
        selectedCallback={this.handleSelectDesvincularPontoDaAcao}
        pontosProblematicos={item.pontosProblematicos.filter((x) => x.id > 0)}
        relatorioAcompanhamentoId={this.props.model.getModel('id').value}
      />
    );
  }

  handleSelectDesvincularPontoDaAcao(pontosProblematicos) {
    let model = this.props.model;
    const idsPontos =
      pontosProblematicos && pontosProblematicos.map((ponto) => ponto.id);
    const acaoId =
      model.getModel('acaoSelecionadaAtual').value &&
      model.getModel('acaoSelecionadaAtual').value.id;
    let pontosModel = model.getModel('pontosProblematicos').value;

    http
      .post(`/Acao/DesvincularPontoProblematico`, {
        acaoId,
        pontosProblematicosId: idsPontos,
      })
      .then(() => {
        pontosModel &&
          pontosModel.map((ponto) => {
            if (idsPontos.includes(ponto.id)) {
              const acoes = ponto.acoes;
              const index =
                acoes && acoes.findIndex((prop) => prop.id === acaoId);

              if (index != -1) ponto.acoes.splice(index, 1);
            }
          });

        model.getModel('pontosProblematicos').requestChange(pontosModel);
        this.refreshModel();
      });
  }

  renderCustomFields(disabled, model) {
    return (
      model.getModel('campos').value &&
      model.getModel('campos').value.length > 0 && (
        <DesenharCampoCustomizado
          model={model.getModel('campos')}
          disabled={disabled}
        />
      )
    );
  }

  renderRelatorioHeaderAboveTabs({
    model,
    resources,
    intl,
    isNew,
    ehPlanejamentoInicial,
    disabled,
    isConsultaExterna,
    configuracao,
    farol,
  }) {
    const showCopiar =
      configuracao.value &&
      configuracao.value.habilitarCopiar.id !== 0 &&
      !isConsultaExterna &&
      model.getModel('status').value &&
      model.getModel('status').value.id !== 1;

    const descricaoTipoRelatorio =
      !ehPlanejamentoInicial &&
      !isNew &&
      capitalizeFirstLetter(
        intl.formatMessage({
          id: model.getModel('ehValorAcumulado').value ? 'acumulado' : 'periodo',
        })
      );

    return (
      <>
        {configuracao.value &&
          configuracao.value.habilitarOrientacao.id !== 0 &&
          configuracao.value.orientacaoTexto && (
            <Row className="mb-0">
              <Col md={12}>
                <InfoPanel
                  customMessage
                  className={baseClasses.infoPanelOrientacao}
                  onDismiss={this.handleInfoPanelDismiss}
                >
                  {orientacaoTextoForEdit(configuracao.value.orientacaoTexto)}
                </InfoPanel>
              </Col>
            </Row>
          )}

        {model.getModel('status').value &&
          model.getModel('status').value.id === 2 && (
            <Row className="mb-3">
              <Col md={12}>
                <UncontrolledAlert color="warning" className="mb-0">
                  {intl.formatMessage(
                    { id: 'relatorioDefasadoValoresAntigos' },
                    {
                      relatoriodeacompanhamento:
                        resources.relatoriodeacompanhamento,
                    }
                  )}
                </UncontrolledAlert>
              </Col>
            </Row>
          )}

        <Row className={baseClasses.resumoLinhaItem}>
          <Col md={12}>
            <span className={baseClasses.campoResumoInline}>
              <span className={baseClasses.campoResumoLabel}>
                <FormattedMessage id="item" />
              </span>{' '}
              <ItemLink
                className={`${baseClasses.campoResumoCopiar} text-primary`}
                handleCloseEdit={this.props.handleCloseEdit}
                model={model.getModel('item')}
                isConsultaExterna={isConsultaExterna}
              />
            </span>
          </Col>
        </Row>

        {isNew && ehPlanejamentoInicial && (
          <Row className="mb-3">
            <Col md={4}>
              <DatePicker
                year
                timeFormat={false}
                model={{
                  label: intl.formatMessage({ id: 'periodo' }),
                  value: model.getModel('dataPeriodo').value,
                  requestChange: model.getModel('dataPeriodo').requestChange,
                }}
              />
            </Col>
          </Row>
        )}

        <Row className={baseClasses.resumoLinhaValores}>
          <Col md={12}>
            <div className="d-flex flex-wrap align-items-center">
              {descricaoTipoRelatorio && (
                <span className={baseClasses.campoResumoInline}>
                  <span className={baseClasses.campoResumoLabel}>
                    <FormattedMessage id="tipo" />
                  </span>{' '}
                  <span className={baseClasses.campoResumoValor}>
                    {descricaoTipoRelatorio}
                  </span>
                </span>
              )}
              {!isNew && (
                <span className={baseClasses.campoResumoInline}>
                  <span className={baseClasses.campoResumoLabel}>
                    <FormattedMessage id="periodo" />
                  </span>{' '}
                  <span className={baseClasses.campoResumoValor}>
                    {model.getModel('descricaoPeriodo').value}
                  </span>
                </span>
              )}
              {!ehPlanejamentoInicial && (
                <>
                  <span className={baseClasses.campoResumoInline}>
                    <span className={baseClasses.campoResumoLabel}>
                      {capitalizeFirstLetter(resources.meta)}
                    </span>{' '}
                    <span className={baseClasses.campoResumoValor}>
                      {model.getModel('meta').value}
                    </span>
                  </span>
                  <span className={baseClasses.campoResumoInline}>
                    <span className={baseClasses.campoResumoLabel}>
                      <FormattedMessage id="realizado" />
                    </span>{' '}
                    <span className={baseClasses.campoResumoValor}>
                      {model.getModel('realizado').value}
                    </span>
                  </span>
                  <span className={baseClasses.campoResumoInline}>
                    <span className={baseClasses.campoResumoLabel}>
                      <FormattedMessage id="desvio" />
                    </span>{' '}
                    <span className={baseClasses.campoResumoValor}>
                      {model.getModel('desvio').value}
                    </span>
                  </span>
                  <span className={baseClasses.campoResumoInline}>
                    <span className={baseClasses.campoResumoLabel}>
                      {capitalizeFirstLetter(resources.farol)}
                    </span>{' '}
                    <span className={baseClasses.campoResumoValor}>
                      {farol && (
                        <SvgIcon
                          style={{ display: 'inline', marginLeft: 4 }}
                          icon={farol.icone}
                          color={farol.cor}
                          colorHover={farol.cor}
                          title={farol.descricao}
                        ></SvgIcon>
                      )}
                    </span>
                  </span>
                </>
              )}
              <span className={baseClasses.campoResumoInlineControle}>
                <span className={baseClasses.campoResumoLabel}>
                  <FormattedMessage id="status" />
                </span>{' '}
                <span className={baseClasses.campoResumoValor}>
                  <StatusRelatorioLookup
                  showLabel={false}
                  style={{ marginBottom: 0, minWidth: 140 }}
                  model={{
                    value: model.getModel('status').value,
                    requestChange: this.handleChangeStatus,
                  }}
                  data={{ idSelecionaveis: [0, 1, 2] }}
                  clearable={false}
                  disabled={disabled || model.getModel('status').value.id !== 2}
                />
                </span>
              </span>
              <span className={baseClasses.campoResumoInlineControle}>
                <span className={baseClasses.campoResumoLabel}>
                  <FormattedMessage id="label.anexos" />
                </span>{' '}
                <span className={baseClasses.campoResumoValor}>
                  <ArquivoUploadAnexos
                    tipoAnexo={6}
                    classNameFormGroup="d-inline-flex align-items-center mb-0"
                    formGroupStyle={{ marginBottom: 0 }}
                    maxWidth="none"
                    model={{
                      value: model.getModel('arquivos').value,
                      requestChange: model.getModel('arquivos').requestChange,
                    }}
                    disabledClick={disabled || isConsultaExterna}
                  />
                </span>
              </span>
              {showCopiar && (
                <>
                  <span
                    id="copiarRelatorioAcompanhamento"
                    data-tip
                    data-for="copiarRelatorioAcompanhamento"
                    className={`${baseClasses.campoResumoInline} ${baseClasses.campoResumoCopiar} text-primary ml-md-auto`}
                    onClick={() => this.handleOpenRelatorios()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.handleOpenRelatorios();
                      }
                    }}
                  >
                    {capitalizeFirstLetter(
                      intl.formatMessage({ id: 'label.copiar' })
                    )}
                  </span>
                  <ReactTooltip
                    id="copiarRelatorioAcompanhamento"
                    place="top"
                    aria-haspopup="true"
                  >
                    <FormattedMessage id="copiarRelatorioPreenchidoParaRelatorioPendente" />
                  </ReactTooltip>
                </>
              )}
            </div>
          </Col>
        </Row>
      </>
    );
  }

  renderRelatorioResumoCategoryLayout({
    model,
    resources,
    intl,
    configuracao,
    grafico,
    item,
    itemId,
    disabled,
    isConsultaExterna,
    createChildItemsAcao,
    exportApresentacao,
  }) {
    const sections = [];
    const reportId = this.props.model.getModel('id').value;
    const hasReportId = reportId && reportId != 0;

    if (
      configuracao.value &&
      configuracao.value.habilitarGrafico.id !== 0 &&
      grafico &&
      item
    ) {
      sections.push({
        id: RA_SEC_GRAFICO,
        header: capitalizeFirstLetter(
          intl.formatMessage({ id: 'label.grafico' })
        ),
        children: (
          <div style={{ width: '100%', height: 400 }} id="graficoRelatorioPag">
            <GraficoValores item={item} grafico={grafico} />
          </div>
        ),
      });
    }

    if (
      configuracao.value &&
      configuracao.value.habilitarPlanejado.id !== 0
    ) {
      sections.push({
        id: RA_SEC_PLANEJADO,
        header: capitalizeFirstLetter(resources.planejado),
        children: (
          <div
            className={`text-editor ${
              disabled ? 'containerDisabled' : ''
            }`}
          >
            <ReactQuill
              value={
                model.getModel('planejado') &&
                model.getModel('planejado').value
              }
              onChange={this.handleChangeText.bind(this, 'planejado')}
              readOnly={disabled || isConsultaExterna}
              theme="snow"
              formats={this.formats}
              modules={this.modules}
            />
          </div>
        ),
      });
    }

    if (
      configuracao.value &&
      configuracao.value.habilitarExecutado.id !== 0
    ) {
      sections.push({
        id: RA_SEC_EXECUTADO,
        header: capitalizeFirstLetter(resources.executado),
        children: (
          <div
            className={`text-editor ${
              disabled ? 'containerDisabled' : ''
            }`}
          >
            <ReactQuill
              value={
                model.getModel('executado') &&
                model.getModel('executado').value
              }
              theme="snow"
              onChange={this.handleChangeText.bind(this, 'executado')}
              readOnly={disabled || isConsultaExterna}
              formats={this.formats}
              modules={this.modules}
            />
          </div>
        ),
      });
    }

    if (configuracao.value && configuracao.value.habilitarFatos.id !== 0) {
      sections.push({
        id: RA_SEC_FATOS,
        header: capitalizeFirstLetter(resources.fatos),
        children: (
          <div
            className={`text-editor ${
              disabled ? 'containerDisabled' : ''
            }`}
          >
            <ReactQuill
              value={
                model.getModel('fatos') && model.getModel('fatos').value
              }
              theme="snow"
              onChange={this.handleChangeText.bind(this, 'fatos')}
              readOnly={disabled || isConsultaExterna}
              formats={this.formats}
              modules={this.modules}
            />
          </div>
        ),
      });
    }

    const customFields = this.renderCustomFields(disabled, model);
    if (customFields) {
      sections.push({
        id: RA_SEC_INFO_COMPLEMENTARES,
        header: capitalizeFirstLetter(
          intl.formatMessage({ id: 'informacoesComplementares' }),
        ),
        children: customFields,
      });
    }

    if (
      configuracao.value &&
      configuracao.value.habilitarEstratificacaoPareto.id !== 0 &&
      model.getModel('contadorInicialTabelaEstratificacao').value
    ) {
      sections.push({
        id: RA_SEC_ESTRATIFICACAO,
        header: capitalizeFirstLetter(
          intl.formatMessage({ id: 'label.estratificacaoPareto' })
        ),
        collapseCallback: this.handleParetoSectionCollapse,
        children: (
          <EstratificacaoPareto
            item={model.getModel('item').value}
            estratificacoesModel={model.getModel('estratificacoesValores')}
            contadorInicial={
              model.getModel('contadorInicialTabelaEstratificacao').value
            }
            chartRemountKey={this.state.paretoChartKey}
          />
        ),
      });
    }

    if (
      configuracao.value &&
      configuracao.value.habilitarPontosProblematicos.id !== 0 &&
      hasReportId &&
      !this.state.isLoading
    ) {
      sections.push({
        id: RA_SEC_CAUSA,
        header: capitalizeFirstLetter(resources.pontosproblematicos),
        children: (
          <PontosProblematicosList
            saved={this.state.saved}
            showExport={false}
            showNew={!isConsultaExterna && !disabled}
            showMenu={!isConsultaExterna && !disabled}
            disabled={isConsultaExterna || disabled}
            showDelete={!isConsultaExterna && !disabled}
            showAnaliseCausa={!isConsultaExterna && !disabled}
            model={model}
            refreshModel={this.refreshModel}
            isConsultaExterna={isConsultaExterna}
            omitOuterPanel
          />
        ),
      });
    }

    if (
      configuracao.value &&
      configuracao.value.habilitarAcoes.id !== 0 &&
      hasReportId &&
      !this.state.isLoading
    ) {
      sections.push({
        id: RA_SEC_PLANO_ACAO,
        header: capitalizeFirstLetter(resources.planodeacao),
        children: (
          <AcaoList
            saved={this.state.saved}
            configuracao={configuracao}
            exportApresentacao={exportApresentacao}
            kanban={!exportApresentacao}
            showToolBar={!exportApresentacao}
            formatoKanbanDefault={false}
            columnsCard={this.state.columnsCard}
            columnsKanban={this.state.columnsKanban}
            allColumnsCard={this.state.allColumnsCard}
            allColumnsKanban={this.state.allColumnsKanban}
            showDelete={!disabled}
            onDeleting={this.handleDeleteClick}
            params={{
              relatorioId: this.props.model.getModel('id').value,
              itemId: itemId,
            }}
            showPrevious={false}
            showLink={true}
            showNew={!isConsultaExterna && !disabled}
            showSearch={false}
            naoSalvarFiltro={true}
            showMenuAcao={false}
            disabled={isConsultaExterna || disabled}
            showExport={false}
            createChildItems={createChildItemsAcao}
            refreshModel={this.refreshModel}
            tag={2005}
            isConsultaExterna={isConsultaExterna}
            omitOuterPanel
          ></AcaoList>
        ),
      });
    }

    if (
      configuracao.value &&
      configuracao.value.habilitarProjetos.id !== 0 &&
      hasReportId &&
      !this.state.isLoading
    ) {
      sections.push({
        id: RA_SEC_PROJETOS,
        header: capitalizeFirstLetter(resources.projetos),
        children: (
          <ProjetosList
            saved={this.state.saved}
            model={this.props.model.getModel('projetos')}
            params={{
              relatorioId: this.props.model.getModel('id').value,
            }}
            showSearch={!isConsultaExterna}
            isConsultaExterna={isConsultaExterna}
          ></ProjetosList>
        ),
      });
    }

    const categories = [
      {
        id: RA_CAT_ANALISE,
        label: capitalizeFirstLetter(
          intl.formatMessage({ id: 'fato' }),
        ),
        sectionIds: [
          RA_SEC_GRAFICO,
          RA_SEC_PLANEJADO,
          RA_SEC_EXECUTADO,
          RA_SEC_FATOS,
          RA_SEC_INFO_COMPLEMENTARES,
          RA_SEC_ESTRATIFICACAO,
        ],
      },
      {
        id: RA_CAT_CAUSA,
        label: capitalizeFirstLetter(
          intl.formatMessage({ id: 'label.causa' }),
        ),
        sectionIds: [RA_SEC_CAUSA],
      },
      {
        id: RA_CAT_ACAO,
        label: capitalizeFirstLetter(resources.acao),
        sectionIds: [RA_SEC_PLANO_ACAO, RA_SEC_PROJETOS],
      },
    ];

    if (!sections.length) {
      return null;
    }

    return (
      <ModalFormCategoryLayout
        categories={categories}
        sections={sections}
        layoutSyncKey={this.state.layoutSyncKey}
      />
    );
  }

  render() {
    let {
      model,
      resources,
      intl,
      divIdToExport,
      parameters,
      ehPlanejamentoInicial,
      isConsultaExterna,
    } = this.props;

    let configuracao = model.getModel('configuracao');
    let detail = this.props.mode && this.props.mode == 'detail';
    const isNew = this.props.model.getModel('id').value == 0;

    let itemId =
      parameters && parameters.itemId
        ? parameters.itemId
        : this.props.model.getModel('item').value &&
          this.props.model.getModel('item').value.id;
    let allowEdit = model.getModel('permissions').getModel('allowEdit').value;
    let disabled = !allowEdit ? true : detail;

    let grafico = model.getModel('grafico').value;
    let item = model.getModel('item').value;
    let farol = model.getModel('farolItem').value;

    let createChildItemsAcao = [
      {
        label: (
          <FormattedMessage
            id="desvincularPontoProblematico"
            values={{
              pontosproblematicos: this.props.resources.pontosproblematicos,
            }}
          />
        ),
        icon: 'Unlink',
        visible: () => true,
        onClick: this.handleDesvincularPontoProblematicoClick,
      },
    ];

    return (
      <Suspense fallback={<LoadingScreen />}>
        <LoadingContainer
          isLoading={this.state.isLoading}
          className="modalFormCategoryLayoutHost"
        >
          {this.renderRelatorioHeaderAboveTabs({
            model,
            resources,
            intl,
            isNew,
            ehPlanejamentoInicial,
            disabled,
            isConsultaExterna,
            configuracao,
            farol,
          })}
          <Tabs onSelect={this.handleTabSelect}>
            <Tab key="1" label={<FormattedMessage id="label.resumo" />}>
              <div
                id={divIdToExport}
                className="modalFormCategoryLayoutHost__tabBody"
              >
                {this.renderRelatorioResumoCategoryLayout({
                  model,
                  resources,
                  intl,
                  configuracao,
                  grafico,
                  item,
                  itemId,
                  disabled,
                  isConsultaExterna,
                  createChildItemsAcao,
                  exportApresentacao: this.props.exportApresentacao,
                })}
              </div>
            </Tab>
            <Tab key="2" label={capitalizeFirstLetter(resources.historico)}>
              <RelatorioModalTabScroll
                layoutSyncKey={this.state.historicoRefreshToken}
              >
                <Historico
                  url={`/RelatorioAcompanhamento/ObterHistorico`}
                  id={this.props.model.getModel('id').value}
                  refreshToken={this.state.historicoRefreshToken}
                />
              </RelatorioModalTabScroll>
            </Tab>
          </Tabs>
        </LoadingContainer>
      </Suspense>
    );
  }
}

function RelatorioModalTabScroll({ children, layoutSyncKey = 0 }) {
  const mainRef = useRef(null);
  useCollapsesScrollHeight(mainRef, [layoutSyncKey]);

  return (
    <div className="modalFormCategoryLayoutHost__tabBody">
      <div ref={mainRef} className="modalFormCategoryLayoutHost__tabMain">
        {children}
      </div>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    resources: state.user.termos,
    user: state.user,
    moduloAtual: state.user.moduloAtual,
    tiposAnexos: state.user.tiposAnexos,
    viewType: state.viewType,
  };
}
export default injectIntl(connect(mapStateToProps)(FormEdit));
